import { getApiUrl } from "src/common/api/getUrl";
import { dataParser } from "src/common/dataParser";
import { mdmLoader } from "src/common/mdmLoader";
import { rest } from "src/common/rest";
import { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import z from "zod";

import { ZAttrMeta } from "../attribute";
import { safeAttrValues } from "../attribute/safeAttrValues";
import { ZSafeDataMap, ZUnsafeDataMap } from "../attribute/ZAttrValue";
import { ZSpecification } from "../specification";
import { makeAttrMetaMap } from "../specification/makeAttrMetaMap";
import {
  ValueCatalogCardsFilter,
  ValueCatalogFilter,
} from "./ValueCatalogFilter";
import {
  ValueCatalogSave,
  ZValueCatalog,
  zValueCatalog,
} from "./ZValueCatalog";
import {
  ZValueCatalogCard,
  zValueCatalogCard,
  ZValueCatalogCardSafe,
} from "./ZValueCatalogCard";

export const urlValueCatalog = (path = "") =>
  getApiUrl(`/core/catalog-values${path}`);

export const valueCatalogsLoader = (): RtLoaderFn<
  ZValueCatalog,
  ValueCatalogFilter
> => mdmLoader(zValueCatalog, urlValueCatalog());

export const loadValueCatalog = async (id: string): Promise<ZValueCatalog> => {
  const resp = await rest.get(urlValueCatalog(`/${id}`));
  return dataParser(() => zValueCatalog.parse(resp.data), "loadValueCatalog");
};

export const createValueCatalog = async (
  data: ValueCatalogSave,
): Promise<ZValueCatalog> => {
  const resp = await rest.post(urlValueCatalog(), data);
  return dataParser(() => zValueCatalog.parse(resp.data), "createValueCatalog");
};

export const saveValueCatalog = async (
  id: string,
  data: ValueCatalogSave,
): Promise<ZValueCatalog> => {
  const resp = await rest.put(urlValueCatalog(`/${id}`), data);
  return dataParser(() => zValueCatalog.parse(resp.data), "createValueCatalog");
};

export const deleteValueCatalog = async (id: string) => {
  await rest.delete(urlValueCatalog(`/${id}`));
};

export const loadValueCatalogCard = async (
  valueCatalogId: string,
): Promise<ZValueCatalogCard> => {
  const resp = await rest.get(urlValueCatalog(`/cards/${valueCatalogId}`));
  return dataParser(
    () => zValueCatalogCard.parse(resp.data),
    "Загрузка карточки каталога значений",
  );
};

export const createValueCatalogCard = async (
  valueCatalogId: string,
  data: ZUnsafeDataMap,
): Promise<ZValueCatalogCard> => {
  const resp = await rest.post(urlValueCatalog(`/${valueCatalogId}/cards`), {
    data,
  });
  return dataParser(
    () => zValueCatalogCard.parse(resp.data),
    "Создание карточки каталога значений",
  );
};

export const updateValueCatalogCard = async (
  id: string,
  data: ZUnsafeDataMap,
): Promise<ZValueCatalogCard> => {
  const resp = await rest.put(urlValueCatalog(`/cards/${id}`), { data });
  return dataParser(
    () => zValueCatalogCard.parse(resp.data),
    "Сохранение карточки каталога значений",
  );
};

export const deleteValueCatalogCard = async (id: string) => {
  await rest.delete(urlValueCatalog(`/cards/${id}`));
};

export const valueCatalogCardsLoader = (
  valueCatalogId: string,
  spec: ZSpecification,
): RtLoaderFn<ZValueCatalogCardSafe, ValueCatalogCardsFilter> => {
  const metaMap = makeAttrMetaMap(spec);
  const zResult = z.object({
    content: zValueCatalogCard.array(),
    pagination: z.object({
      totalElements: z.number(),
    }),
  });

  return async (request) => {
    const params = {
      ...request.filters,
      page: request.page,
      size: request.pageSize,
    };
    const url = urlValueCatalog(`/${valueCatalogId}/cards`);
    const resp = await rest.get(url, { params });
    const { content, pagination } = dataParser(
      () => zResult.parse(resp.data),
      url,
    );
    const rows = content.map(({ data, ...fields }) => ({
      ...fields,
      data: makeSafeValuesCatalogData(data, spec.name, metaMap),
    }));
    return {
      rows,
      totalRows: pagination.totalElements,
    };
  };
};

export const makeSafeValuesCatalogData = (
  unsafe: ZUnsafeDataMap,
  specName: string,
  metaMap: Record<string, ZAttrMeta>,
): ZSafeDataMap => ({
  [specName]: safeAttrValues(unsafe[specName] ?? {}, metaMap),
});
